import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ChangeEvent, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useAccount } from '@/account/useAccount';
import { useAppConfigContext } from '@/context/AppConfig';
import type { SearchReportResponseData } from '@/reports/searchReport';
import { vehicleSelections } from '@/reports/shared/VendorData/VendorData';
import { vehicleSearch, SearchParams } from '@/reports/vehicle/vehicleSearch';
import {
  Autocomplete,
  Grid,
  Text,
  Input,
  InputLabel,
  Button,
  Box,
} from '@/theme/index';
import { getVehicleTrims, useScreenSize } from '@/utils/index';
import { schemaYmmSearchForm } from '@/utils/schemaForms/schemaForms';
import { VehicleSelection, Make } from './ymmSearch';
import { StyledUnderlineButton, StyledYmmInputsContainer } from '../components';
import {
  getMakes,
  getModels,
  getYears,
  isValidMake,
  isValidModel,
  isValidTrim,
  isValidYear,
  VehicleSearchProps,
} from '../utils';
/**
 * This component is responsible for providing users with the ability to search for vehicles by Year, Make and Model.
 * Once the search is successful provide the necessary information to redirect to the report.
 */

interface YMMSearchFormProps extends VehicleSearchProps {
  showTrim?: boolean;
  showSubmitButton?: boolean;
  handleUpdateYMMForm?: ({
    year,
    make,
    model,
    trim,
  }: Partial<{
    year: string;
    make: string;
    model: string;
    trim?: string;
  }>) => void;
  isForm?: boolean;
  widerDropdowns?: boolean;
}

export function YMMSearchForm({
  onSuccess,
  onError,
  searchButtonText = 'Search',
  clearFields,
  mobileView = false,
  optionalProps = {},
  searchButtonSize = '15%',
  placement = 'header',
  showTrim = true,
  showSubmitButton = true,
  isForm = true,
  widerDropdowns = false,
  handleUpdateYMMForm,
}: YMMSearchFormProps) {
  const { data: account } = useAccount(true);
  const { logError, trackEventGA4 } = useAppConfigContext();
  const [models, setModels] = useState<string[]>([]);
  const [makes, setMakes] = useState<string[]>([]);
  const [trims, setTrims] = useState<string[]>([]);
  const [makesObj, setMakesObj] = useState<Make[]>([]);
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const years = getYears();
  const { isMobile } = useScreenSize();
  const mobileHeight = mobileView && placement === 'dashboard';

  const vehicleQuery = useMutation<
    SearchReportResponseData,
    Error,
    SearchParams
  >(vehicleSearch, {
    onSuccess: (data) => {
      onSuccess &&
        onSuccess({
          permalink: data.report.permalink,
          reportType: 'vehicle',
          searchParams: '',
        });
    },
    onError: (error) => {
      logError('YMM Vehicle Search', error);
      onError && onError(error);
    },
  });
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
    control,
    setValue,
    getValues,
    clearErrors,
  } = useForm<{
    year: string;
    make: string;
    model: string;
    trim: string;
  }>({
    mode: 'onSubmit',
    resolver: yupResolver(schemaYmmSearchForm),
    defaultValues: {
      year: '',
      make: '',
      model: '',
      trim: '',
    },
  });
  const { data } = useQuery<VehicleSelection>({
    queryKey: ['vehicleSelections'],
    queryFn: () => vehicleSelections(),
  });

  useEffect(() => {
    if (data?.selections) {
      const { selections } = data;
      setMakesObj(selections.makes);
      setMakes(getMakes(selections.makes));
    }
  }, [data]);

  useEffect(() => {
    setModels(getModels(selectedMake, makesObj));
    clearErrors('model');
  }, [selectedMake]);

  useEffect(() => {
    setTrims(getVehicleTrims(selectedMake, selectedModel, makesObj));
  }, [selectedModel]);

  useEffect(() => {
    clearErrors('trim');
  }, [selectedMake, selectedModel]);

  const onMakeInput = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target as HTMLInputElement;
    setSelectedMake(value);
    setValue('make', value);
    setValue('model', '');
    setValue('trim', '');
    clearErrors('make');
  };

  const onMakeBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { value } = event.target as HTMLInputElement;
    if (!isValidMake(makes, value)) {
      setSelectedMake('');
      setSelectedModel('');
      setValue('make', '');
      setValue('model', '');
      setValue('trim', '');
    }
  };

  const onModelInput = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target as HTMLInputElement;
    setSelectedModel(value);
    setValue('model', value);
    setValue('trim', '');
    clearErrors('model');
  };

  const onModelBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { value } = event.target as HTMLInputElement;
    const isValid = isValidModel(models, value);

    if (isValid && handleUpdateYMMForm) {
      handleUpdateYMMForm({
        year: getValues('year'),
        make: getValues('make'),
        model: getValues('model'),
      });
    }

    if (!isValid) {
      setSelectedModel('');
      setValue('model', '');
      setValue('trim', '');
    }
  };

  const onTrimInput = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target as HTMLInputElement;
    setValue('trim', value);
  };

  const onTrimBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { value } = event.target as HTMLInputElement;
    const isValid = isValidTrim(trims, value);

    if (isValid && handleUpdateYMMForm) {
      handleUpdateYMMForm({
        trim: getValues('trim'),
      });
    }

    if (!isValid) {
      setValue('trim', '');
    }
  };

  const onYearChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    setValue('year', target.value);
    clearErrors('year');
  };

  const onYMMSearchVehicle = async ({
    year,
    make,
    model,
    trim,
  }: SearchParams) => {
    const userInRecycling = account?.account.user_info.user_in_recycling;
    const hasRecyclingReports =
      !!account?.account.subscription_info.recycling_reports_remaining;

    if (userInRecycling && !hasRecyclingReports) {
      window.dispatchEvent(new CustomEvent('openRecyclingModal'));
      return;
    }

    trackEventGA4({
      event_name: 'report_search',
      report_type: 'vehicle',
      search_by: 'year_make_model',
      search_from: `${placement}_search`,
    });
    vehicleQuery.mutate({ year, make, model, trim });
  };

  const additionalDropdownContainerStyles = {
    ...(widerDropdowns && !isMobile
      ? {
          width: '25%',
          minWidth: '25%',
          maxWidth: '25%',
        }
      : {}),
    ...(isMobile && !(placement === 'dashboard')
      ? {
          height: '88px',
        }
      : {}),
  };

  return (
    <Box
      component={isForm ? 'form' : 'div'}
      data-testid="ymm-search-form"
      onSubmit={handleSubmit(onYMMSearchVehicle)}
      aria-label="form"
      {...(isForm ? { autoComplete: 'off' } : {})}
      style={optionalProps?.formStyle}
    >
      <StyledYmmInputsContainer
        container
        spacing={2}
        sx={{
          alignItems: {
            sm: `${optionalProps?.makeLabelHidden ? 'start' : 'center'}`,
          },
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Grid
          item
          sm={optionalProps?.yearWidth ?? 2}
          sx={{
            height: mobileHeight
              ? 'auto'
              : optionalProps?.selectHeight ?? '105px',
            ...additionalDropdownContainerStyles,
          }}
          className={placement === 'dashboard' ? '' : 'input-container'}
        >
          {optionalProps?.makeLabelHidden !== true && (
            <InputLabel htmlFor="year-select" sx={{ fontWeight: 'bold' }}>
              Year
            </InputLabel>
          )}
          <Controller
            control={control}
            name="year"
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                {...register('year', { required: true })}
                fullWidth
                options={years}
                id="year-select"
                defaultValue=""
                value={value}
                disableClearable
                renderInput={(params) => (
                  <Input
                    {...params}
                    placeholder="Year"
                    variant="outlined"
                    error={Boolean(errors.year?.message)}
                    onInput={onYearChange}
                    onBlur={() => {
                      if (!isValidYear(years, value)) {
                        onChange('');
                        setValue('year', '');
                      }
                    }}
                  />
                )}
                onChange={(event, data) => onChange(data)}
                isOptionEqualToValue={(option, value) =>
                  value === '' || option === value
                }
              />
            )}
          />
          {errors.year?.message && (
            <Text variant="body2" color="error">
              {errors.year.message}
            </Text>
          )}
        </Grid>
        <Grid
          item
          sm={optionalProps?.makeWidth ?? 2}
          sx={{
            height: mobileHeight
              ? 'auto'
              : optionalProps?.selectHeight ?? '105px',
            ...additionalDropdownContainerStyles,
          }}
          className={placement === 'dashboard' ? '' : 'input-container'}
        >
          {optionalProps?.makeLabelHidden !== true && (
            <InputLabel htmlFor="make-select" sx={{ fontWeight: 'bold' }}>
              Make
            </InputLabel>
          )}
          <Controller
            control={control}
            name="make"
            render={({ field }) => (
              <Autocomplete
                {...register('make', { required: true })}
                {...field}
                fullWidth
                options={makes}
                id="make-select"
                defaultValue=""
                disableClearable
                renderInput={(params) => (
                  <Input
                    {...params}
                    placeholder="Make"
                    variant="outlined"
                    error={Boolean(errors.make?.message)}
                    onBlur={onMakeBlur}
                  />
                )}
                onInput={onMakeInput}
                onChange={(event, data) => {
                  setSelectedMake(data);
                  field.onChange(data);
                }}
                isOptionEqualToValue={(option, value) =>
                  value === '' || option === value
                }
              />
            )}
          />
          {errors.make?.message && (
            <Text variant="body2" color="error">
              {errors.make.message}
            </Text>
          )}
        </Grid>
        <Grid
          item
          sm={optionalProps?.modelWidth ?? 2}
          sx={{
            height: mobileHeight
              ? 'auto'
              : optionalProps?.selectHeight ?? '105px',
            ...additionalDropdownContainerStyles,
          }}
          className={placement === 'dashboard' ? '' : 'input-container'}
        >
          {optionalProps?.modelLabelHidden !== true && (
            <InputLabel
              htmlFor="model-select"
              sx={{
                fontWeight: 'bold',
              }}
            >
              Model
            </InputLabel>
          )}
          <Controller
            control={control}
            name="model"
            render={({ field }) => (
              <Autocomplete
                {...register('model', { required: true })}
                {...field}
                fullWidth
                options={models}
                id="model-select"
                defaultValue=""
                getOptionLabel={(option) => option}
                disableClearable
                disabled={!selectedMake}
                renderInput={(params) => (
                  <Input
                    {...params}
                    placeholder="Model"
                    variant="outlined"
                    error={Boolean(errors.model?.message)}
                    onBlur={onModelBlur}
                  />
                )}
                onInput={onModelInput}
                onChange={(event, data) => {
                  setSelectedModel(data);
                  field.onChange(data);
                }}
                isOptionEqualToValue={(option, value) =>
                  value === '' || option === value
                }
              />
            )}
          />
          {errors.model?.message && selectedMake && (
            <Text variant="body2" color="error">
              {errors.model.message}
            </Text>
          )}
        </Grid>
        {showTrim && (
          <Grid
            item
            sm={optionalProps?.trimWidth ?? 2}
            sx={{
              height: mobileHeight
                ? 'auto'
                : optionalProps?.selectHeight ?? '105px',
              ...(Boolean(optionalProps?.trimHidden) && { display: 'none' }),
              ...additionalDropdownContainerStyles,
            }}
            className={placement === 'dashboard' ? '' : 'input-container'}
          >
            {optionalProps?.trimLabelHidden !== true && (
              <InputLabel htmlFor="trim-select" sx={{ fontWeight: 'bold' }}>
                Trim
              </InputLabel>
            )}
            <Controller
              control={control}
              name="trim"
              render={({ field: { value, onChange } }) => (
                <Autocomplete
                  {...register('trim', { required: true })}
                  fullWidth
                  options={trims}
                  id="trim-select"
                  defaultValue=""
                  value={value}
                  disableClearable
                  disabled={!selectedModel}
                  renderInput={(params) => (
                    <Input
                      {...params}
                      placeholder="Trim"
                      variant="outlined"
                      error={Boolean(errors.trim?.message)}
                      onBlur={onTrimBlur}
                    />
                  )}
                  onInput={onTrimInput}
                  onChange={(event, data) => onChange(data)}
                  isOptionEqualToValue={(option, value) =>
                    value === '' || option === value
                  }
                />
              )}
            />
            {errors.trim?.message && selectedMake && selectedModel && (
              <Text variant="body2" color="error">
                {errors.trim.message}
              </Text>
            )}
          </Grid>
        )}

        {showSubmitButton && (
          <Grid
            item
            width={{ xs: '100%', md: searchButtonSize }}
            sx={{
              mb: widerDropdowns && !(placement !== 'dashboard') ? 0 : 0.5,
            }}
            pt={widerDropdowns && !(placement !== 'dashboard') ? 0 : 1}
            className={placement !== 'dashboard' ? 'ymm-submit-button' : ''}
          >
            <Button
              fullWidth
              type="submit"
              variant="contained"
              color="success"
              startIcon={optionalProps?.searchStartIcon}
            >
              {searchButtonText}
            </Button>
          </Grid>
        )}
      </StyledYmmInputsContainer>

      {!mobileView && clearFields && (
        <StyledUnderlineButton
          className={placement !== 'dashboard' ? 'ymm-clear-fields' : ''}
          onClick={() => {
            reset();
          }}
        >
          Clear field(s)
        </StyledUnderlineButton>
      )}
    </Box>
  );
}
